import {Routes} from 'constants/routes';

import {lazy, Suspense} from 'react';

import Modal from 'components/Modal/Modal';
import SuspenseLoader from 'components/SuspenseLoader/SuspenseLoader';
import PostGenerator from 'features/postGenerator/PostGenerator';
import BeersList from 'pages/beers/Beers';
import BeersManagerPage from 'pages/beersManager/BeersManager';
import BeerStyles from 'pages/beerStyles/BeerStyles';
import Breweries from 'pages/breweries/Breweries';
import Dashboard from 'pages/dashboard/Dashboard';
import DeleteAccount from 'pages/deleteAccount/DeleteAccount';
import Login from 'pages/login/Login';
import NotFound from 'pages/NotFound/NotFound';
import PageEditors from 'pages/pageEditors/PageEditors';
import PageEditorsEdit from 'pages/pageEditorsEdit/PageEditorsEdit';
import Pub from 'pages/pub/Pub';
import PubPages from 'pages/pubPages/PubPages';
import {ResetPassword} from 'pages/resetPassword/ResetPassword';
import {BrowserRouter as Router, Route, Routes as BrowserRoutes} from 'react-router-dom';

import PrivateRoute from './PrivateRoute';

const Profile = lazy(() => import('pages/profile/Profile'));
const ProfileEdit = lazy(() => import('pages/profileEdit/ProfileEdit'));
const BeerStylesNew = lazy(() => import('pages/beerStylesNew/BeerStylesNew'));
const BeerStylesEdit = lazy(() => import('pages/beerStylesEdit/BeerStylesEdit'));
const BeerStylesView = lazy(() => import('pages/beerStylesView/BeerStylesView'));
const BeersEdit = lazy(() => import('pages/beersEdit/BeersEdit'));
const BeersView = lazy(() => import('pages/beersView/BeersView'));
const BeersNew = lazy(() => import('pages/beersNew/BeersNew'));
const BreweriesEdit = lazy(() => import('pages/breweriesEdit/BreweriesEdit'));
const BreweriesNew = lazy(() => import('pages/breweriesNew/BreweriesNew'));
const BreweriesView = lazy(() => import('pages/breweriesView/BreweriesView'));
const PageEditorsView = lazy(() => import('pages/pageEditorsView/PageEditorsView'));
const PubPagesEdit = lazy(() => import('pages/pubPagesEdit/PubPagesEdit'));
const PubPagesNew = lazy(() => import('pages/pubPagesNew/PubPagesNew'));
const PubPagesView = lazy(() => import('pages/pubPagesView/PubPagesView'));
const PubEdit = lazy(() => import('pages/pubEdit/PubEdit'));
const ForgotPassword = lazy(() => import('pages/forgotPassword/ForgotPassword'));
const Confirmation = lazy(() => import('pages/confirmation/Confirmation'));
const Invite = lazy(() => import('pages/invite/Invite'));
const DownloadApp = lazy(() => import('pages/downloadApp/DownloadApp'));
const PostArchive = lazy(() => import('pages/postArchive/PostArchive'));

export default function AppRouter() {
  return (
    <Router>
      <Suspense fallback={<SuspenseLoader />}>
        <BrowserRoutes>
          <Route
            path={Routes.HOME}
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.PROFILE}
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.PROFILE_EDIT}
            element={
              <PrivateRoute>
                <ProfileEdit />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.BEER_STYLES}
            element={
              <PrivateRoute>
                <BeerStyles />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.BEER_STYLES_NEW}
            element={
              <PrivateRoute>
                <BeerStylesNew />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.BEER_STYLES_EDIT}/:id`}
            element={
              <PrivateRoute>
                <BeerStylesEdit />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.BEER_STYLES_VIEW}/:id`}
            element={
              <PrivateRoute>
                <BeerStylesView />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.BEERS}
            element={
              <PrivateRoute>
                <BeersList />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.BEERS_VIEW}/:id`}
            element={
              <PrivateRoute>
                <BeersView />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.BEERS_NEW}
            element={
              <PrivateRoute>
                <BeersNew />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.BEERS_EDIT}/:id`}
            element={
              <PrivateRoute>
                <BeersEdit />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.EDITORS}
            element={
              <PrivateRoute>
                <PageEditors />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.EDITORS_VIEW}/:id`}
            element={
              <PrivateRoute>
                <PageEditorsView />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.EDITORS_EDIT}/:id`}
            element={
              <PrivateRoute>
                <PageEditorsEdit />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.BREWERIES}
            element={
              <PrivateRoute>
                <Breweries />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.BREWERIES_NEW}
            element={
              <PrivateRoute>
                <BreweriesNew />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.BREWERIES_EDIT}/:id`}
            element={
              <PrivateRoute>
                <BreweriesEdit />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.BREWERIES_VIEW}/:id`}
            element={
              <PrivateRoute>
                <BreweriesView />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.PUB_PAGES}
            element={
              <PrivateRoute>
                <PubPages />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.PUB_PAGES_NEW}
            element={
              <PrivateRoute>
                <PubPagesNew />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.PUB_PAGES_VIEW}/:id`}
            element={
              <PrivateRoute>
                <PubPagesView />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.PUB_PAGES_EDIT}/:id`}
            element={
              <PrivateRoute>
                <PubPagesEdit />
              </PrivateRoute>
            }
          />
          <Route
            path={`${Routes.POST_GENERATOR}`}
            element={
              <PrivateRoute>
                <PostGenerator />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.PUB}
            element={
              <PrivateRoute>
                <Pub />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.PUB_EDIT}
            element={
              <PrivateRoute>
                <PubEdit />
              </PrivateRoute>
            }
          />
          <Route
            path={Routes.BEERS_MANAGER}
            element={
              <PrivateRoute>
                <BeersManagerPage />
              </PrivateRoute>
            }
          />

          <Route path={Routes.LOGIN} element={<Login />} />
          <Route path={Routes.DELETE_ACCOUNT} element={<DeleteAccount />} />
          <Route path={Routes.CONFIRMATION} element={<Confirmation />} />
          <Route path={Routes.FORGOT_PASSWORD} element={<ForgotPassword />} />
          <Route path={Routes.INVITE} element={<Invite />} />
          <Route path={Routes.DOWNLOAD_APP} element={<DownloadApp />} />
          <Route path={Routes.RESET_PASSWORD} element={<ResetPassword />} />
          <Route path={Routes.POST_ARCHIVE} element={<PostArchive />} />
          <Route path="*" element={<NotFound />} />
        </BrowserRoutes>
      </Suspense>
      <Modal />
    </Router>
  );
}
